import { Dispatch } from "redux";

import { loadAndTrack } from "redux-request-loading";
import axios from "axios";

import { Payload } from "@neworbit/redux-helpers";

import { toast } from "@teamprevent/common/toasts";

import { orgStructureApi, OrgStructureDetailModel } from "../orgStructure";

import * as actions from "./actiontypes";
import * as models from "./model";
import { employeeRecordApi } from "./employeeRecordApi";

import {
    CreateEmployeeDocumentPayload,
    LoadEmployeeDocumentsPayload,
    DeleteEmployeeDocumentPayload,
    MergeEmployeeRecordModel
} from "./model";
import { EmployeeDocumentApi } from "./employeeDocumentApi";
import { EmployeeRecordFormModel } from "./schema";

import { push } from "redux-little-router";

export type EmployeeRecordAction =
      ({ type: typeof actions.LOAD_EMPLOYEERECORDS_SUCCESS } & Payload<models.EmployeeRecordsDetailDto>)
    | ({ type: typeof actions.CREATE_EMPLOYEERECORD_SUCCESS } & Payload<models.EmployeeRecordDetailModel>)
    | ({ type: typeof actions.LOAD_EMPLOYEERECORD_DETAIL_SUCCESS } & Payload<models.EmployeeRecordDetailModel>)
    | ({ type: typeof actions.SAVE_EMPLOYEERECORD_SUCCESS } & Payload<models.EmployeeRecordDetailModel>)
    | ({ type: typeof actions.LOAD_ORGSTRUCTURES_SEARCH_SUCCESS_ONE } & Payload<OrgStructureDetailModel[]>)
    | ({ type: typeof actions.LOAD_ORGSTRUCTURES_SEARCH_SUCCESS_TWO } & Payload<OrgStructureDetailModel[]>)
    | ({ type: typeof actions.LOAD_ORGSTRUCTURES_SEARCH_SUCCESS_THREE } & Payload<OrgStructureDetailModel[]>)
    | ({ type: typeof actions.LOAD_ORGSTRUCTURES_SEARCH_SUCCESS_FOUR } & Payload<OrgStructureDetailModel[]>)
    | ({ type: typeof actions.DELETE_EMPLOYEERECORD_SUCCESS } & Payload<any>)
    | ({ type: typeof actions.DEACTIVATE_EMPLOYEERECORD_SUCCESS } & Payload<string>)
    | ({ type: typeof actions.ACTIVATE_EMPLOYEERECORD_SUCCESS } & Payload<string>)
    | ({ type: typeof actions.CREATE_EMPLOYEE_DOCUMENT_SUCCESS } & Payload<CreateEmployeeDocumentPayload>)
    | ({ type: typeof actions.LOAD_EMPLOYEE_DOCUMENTS_SUCCESS } & Payload<LoadEmployeeDocumentsPayload>)
    | ({ type: typeof actions.DELETE_EMPLOYEE_DOCUMENT_SUCCESS } & Payload<DeleteEmployeeDocumentPayload>)
    | ({ type: typeof actions.SET_CLOSE_MONITORING_SUCCESS } & Payload<models.SetCloseMonitoringPayload>)

export const loadOrgStructureSearchSuccessOne = (payload: OrgStructureDetailModel[]): EmployeeRecordAction => ({
    payload,
    type: actions.LOAD_ORGSTRUCTURES_SEARCH_SUCCESS_ONE
});

export const loadOrgStructureSearchSuccessTwo = (payload: OrgStructureDetailModel[]): EmployeeRecordAction => ({
    payload,
    type: actions.LOAD_ORGSTRUCTURES_SEARCH_SUCCESS_TWO
});

export const loadOrgStructureSearchSuccessThree = (payload: OrgStructureDetailModel[]): EmployeeRecordAction => ({
    payload,
    type: actions.LOAD_ORGSTRUCTURES_SEARCH_SUCCESS_THREE
});

export const loadOrgStructureSearchSuccessFour = (payload: OrgStructureDetailModel[]): EmployeeRecordAction => ({
    payload,
    type: actions.LOAD_ORGSTRUCTURES_SEARCH_SUCCESS_FOUR
});

export const loadEmployeeRecordsSuccess = (payload: models.EmployeeRecordsDetailDto): EmployeeRecordAction => ({
    payload,
    type: actions.LOAD_EMPLOYEERECORDS_SUCCESS
});

export const createEmployeeRecordSuccess = (payload: models.EmployeeRecordDetailModel): EmployeeRecordAction => ({
    payload,
    type: actions.CREATE_EMPLOYEERECORD_SUCCESS
});

export const saveEmployeeRecordSuccess = (payload: models.EmployeeRecordDetailModel): EmployeeRecordAction => ({
    payload,
    type: actions.SAVE_EMPLOYEERECORD_SUCCESS
});

export const deleteEmployeeRecordSuccess = (id: string): EmployeeRecordAction => ({
    payload: id,
    type: actions.DELETE_EMPLOYEERECORD_SUCCESS
});

export const deactivateEmployeeRecordSuccess = (id: string): EmployeeRecordAction => ({
    payload: id,
    type: actions.DEACTIVATE_EMPLOYEERECORD_SUCCESS
});

export const activateEmployeeRecordSuccess = (id: string): EmployeeRecordAction => ({
    payload: id,
    type: actions.ACTIVATE_EMPLOYEERECORD_SUCCESS
});

export const createEmployeeDocumentSuccess = (payload: CreateEmployeeDocumentPayload): EmployeeRecordAction => ({
    payload,
    type: actions.CREATE_EMPLOYEE_DOCUMENT_SUCCESS
});

export const loadEmployeeDocumentsSuccess = (payload: LoadEmployeeDocumentsPayload): EmployeeRecordAction => ({
    payload,
    type: actions.LOAD_EMPLOYEE_DOCUMENTS_SUCCESS
});

export const deleteEmployeeDocumentSuccess = (payload: DeleteEmployeeDocumentPayload): EmployeeRecordAction => ({
    payload,
    type: actions.DELETE_EMPLOYEE_DOCUMENT_SUCCESS
});

export function deactivateEmployeeRecord(customerId: string, id: string) {
    return async (dispatch: Dispatch) => {
        await loadAndTrack(dispatch, "deactivateEmployeeRecord", employeeRecordApi.deactivate(customerId, id));
        dispatch(deactivateEmployeeRecordSuccess(id));
    };
}

export function activateEmployeeRecord(customerId: string, id: string) {
    return async (dispatch: Dispatch) => {
        await loadAndTrack(dispatch, "activateEmployeeRecord", employeeRecordApi.activate(customerId, id));
        dispatch(activateEmployeeRecordSuccess(id));
    };
}

export function searchOrgStructures(structureLevel: number, customerId: string, searchString: string, parentLevel?: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, "searchOrgStructures", orgStructureApi.getAllSearch(customerId, searchString, parentLevel));

        switch (structureLevel) {
            case 0:
                dispatch(loadOrgStructureSearchSuccessOne(result));
                break;
            case 1:
                dispatch(loadOrgStructureSearchSuccessTwo(result));
                break;
            case 2:
                dispatch(loadOrgStructureSearchSuccessThree(result));
                break;
            case 3:
                dispatch(loadOrgStructureSearchSuccessFour(result));
                break;
            default:
                throw new Error("Organisation structures limited to 4 you should send levels 0-3 only!");
        }
    };
}

export function createEmployeeRecord(employeeRecord: EmployeeRecordFormModel, path: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.CREATE_EMPLOYEERECORD, employeeRecordApi.create(employeeRecord));
        dispatch(createEmployeeRecordSuccess(result));
        dispatch(push(`${path}/${result.id}`, {}));
    };
}

export function saveEmployeeRecord(employeeRecord: EmployeeRecordFormModel, path?: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.SAVE_EMPLOYEERECORD, employeeRecordApi.save(employeeRecord));

        dispatch(saveEmployeeRecordSuccess(result));
        if (path !== undefined) {
            dispatch(push(`${path}/${result.id}`, {}));
        }
    };
}

export function saveEmployeeRecordFromOhCase(customerId: string, employeeRecord: models.EditEmployeeRecordFromOhCaseModel, path?: string) {
    return async (dispatch: Dispatch) => {
        const result = await loadAndTrack(dispatch, actions.SAVE_EMPLOYEE_RECORD_FROM_OHCASE, employeeRecordApi.saveFromOhCase(customerId, employeeRecord));

        dispatch(saveEmployeeRecordSuccess(result));
        if (path !== undefined) {
            dispatch(push(`${path}/${result.id}`, {}));
        }
    };
}

export function deleteEmployeeRecord(id: any, path: string) {
    return async (dispatch: Dispatch) => {
        const response = await loadAndTrack(dispatch, "deleteEmployeeRecord", employeeRecordApi.delete(id));
        if (!response.success) {
            toast.warning(response.error);
            return;
        }
        dispatch(deleteEmployeeRecordSuccess(id));
        toast.success("Employee record deleted successfully.");
        dispatch(push(`${path}`, {}));
    };
}

export function mergeEmployeeRecord(model: MergeEmployeeRecordModel) {
    return async (dispatch: Dispatch<any>) => {
        await loadAndTrack(dispatch, actions.MERGE_EMPLOYEE_RECORD, employeeRecordApi.mergeEmployeeRecord(model));
    };
}

export const loadEmployeeRecordDetailSuccess = (employeeRecord: models.EmployeeRecordDetailModel) => ({
    payload: employeeRecord,
    type: actions.LOAD_EMPLOYEERECORD_DETAIL_SUCCESS
});

export const buildLoadEmployeeDocuments = (employeeDocumentApi: EmployeeDocumentApi) =>
    ({ customerId, employeeRecordId }: { customerId: string; employeeRecordId: string }) => {
        return async (dispatch: Dispatch) => {
            const result = await loadAndTrack(dispatch, actions.LOAD_EMPLOYEE_DOCUMENTS, employeeDocumentApi.getAll(customerId, employeeRecordId));
            dispatch(loadEmployeeDocumentsSuccess({ employeeRecordId, customerId, documents: result }));
        };
    };

export const loadEmployeeDocuments = buildLoadEmployeeDocuments(new EmployeeDocumentApi(axios));

export const buildDeleteEmployeeDocument = (employeeDocumentApi: EmployeeDocumentApi) =>
    (customerId: string, employeeRecordId: string, employeeDocumentId: string) => {
        return async (dispatch: Dispatch) => {
            await loadAndTrack(dispatch, actions.DELETE_EMPLOYEE_DOCUMENT, employeeDocumentApi.delete(customerId, employeeRecordId, employeeDocumentId));
            dispatch(deleteEmployeeDocumentSuccess({ employeeRecordId, customerId, employeeDocumentId }));
        };
    };

export const deleteEmployeeDocument = buildDeleteEmployeeDocument(new EmployeeDocumentApi(axios));

export function loadEmployeeRecordDetail({ employeeRecordId, customerId }: { employeeRecordId: string; customerId: string }) {
    return async (dispatch: Dispatch, getState: () => models.AppState) => {
        const employeeRecord = getState().employeeRecordsDetail.employeeRecords.filter(c => c.id === employeeRecordId)[0];

        if (employeeRecord === undefined
            || !("detailUpdated" in employeeRecord)
            || employeeRecord.detailUpdated.isBefore(employeeRecord.listUpdated)) {
            const result = await loadAndTrack(dispatch, "loadEmployeeRecordDetail", employeeRecordApi.detail(employeeRecordId, customerId));
            dispatch(loadEmployeeRecordDetailSuccess(result));
        }
    };
}

export const getEmployeeRecordDocumentCount = (customerId: string, employeeRecordId: string): Promise<number> => {
    return new EmployeeDocumentApi(axios).getCount(customerId, employeeRecordId);
};

export const setCloseMonitoring = (customerId: string, employeeRecordId: string, closeMonitoring: boolean) => {
    return async (dispatch: Dispatch) => {
        await loadAndTrack(
            dispatch,
            actions.SET_CLOSE_MONITORING,
            employeeRecordApi.setCloseMonitoring(customerId, employeeRecordId, closeMonitoring)
        );
        dispatch(setCloseMonitoringSuccess({ customerId, employeeRecordId, closeMonitoring }));
    };
};

export const setCloseMonitoringSuccess = (payload: models.SetCloseMonitoringPayload) => ({
    payload,
    type: actions.SET_CLOSE_MONITORING_SUCCESS
});
